/**
 * One-shot profile-completion prompt shown after a community signup
 * (Task 3). Three optional fields: photo, short bio, discipline. A
 * "Skip for now" link bails out without writing any profile data,
 * but in both cases the parent flips
 * privateData.communityProfilePromptDone so the prompt is one-and-done.
 *
 * Built with FinalForm + the project's FieldXxx primitives so the
 * styling matches the other auth/profile flows.
 */

import React, { Component } from 'react';
import { bool, func, string } from 'prop-types';
import { compose } from 'redux';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isUploadImageOverLimitError } from '../../util/errors';
import { propTypes } from '../../util/types';
import {
  Form,
  Avatar,
  Button,
  IconSpinner,
  FieldTextInput,
  FieldSelect,
  FieldMultiSelectInput,
  ImageFromFile,
  Icon,
} from '../../components';
import { disciplinesConfig, rolesConfig } from '../../marketplace-custom-config';

import css from './CommunityProfilePromptForm.module.css';
import { withAvatarPreview } from '../../util/memberAvatar';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000;

class CommunityProfilePromptFormComponent extends Component {
  constructor(props) {
    super(props);
    this.uploadDelayTimeoutId = null;
    this.state = { uploadDelay: false };
    this.submittedValues = {};
  }

  componentDidUpdate(prevProps) {
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            className,
            rootClassName,
            handleSubmit,
            intl,
            invalid,
            pristine,
            uploadInProgress,
            uploadImageError,
            updateInProgress,
            updateProfileError,
            onSkip,
            skipInProgress,
            form,
            values,
            currentUser,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);
          const { profileImage = {} } = values;

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = withAvatarPreview(
            { ...user, profileImage: transientUserProfileImage },
            profileImage.uploadedImage
          );

          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;

          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                rootClassName={css.uploadingImage}
                aspectRatioClassName={css.squareAspectRatio}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          const avatarClasses = classNames(css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });
          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <Avatar
                className={avatarClasses}
                renderSizes="(max-width: 767px) 96px, 240px"
                user={transientUser}
                disableProfileLink
              />
            ) : null;

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <div className={css.avatarContainer}>
                {imageFromFile}
                {avatarComponent}
                <div className={css.changeAvatar}>
                  <Icon name="edit" size={16} className={css.changeAvatarIcon} />
                  <FormattedMessage id="CommunityProfilePromptForm.changeAvatar" />
                </div>
              </div>
            ) : (
              <div className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="CommunityProfilePromptForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="CommunityProfilePromptForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const uploadOverLimit = uploadImageError && isUploadImageOverLimitError(uploadImageError);
          const uploadImageFailed = uploadOverLimit ? (
            <p className={css.error}>
              <FormattedMessage id="CommunityProfilePromptForm.uploadOverLimit" />
            </p>
          ) : uploadImageError ? (
            <p className={css.error}>
              <FormattedMessage id="CommunityProfilePromptForm.uploadFailed" />
            </p>
          ) : null;

          const submitError = updateProfileError ? (
            <p className={css.error}>
              <FormattedMessage id="CommunityProfilePromptForm.updateFailed" />
            </p>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submitDisabled =
            invalid || pristine || uploadInProgress || submitInProgress || skipInProgress;

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.section}>
                <Field
                  accept={ACCEPT_IMAGES}
                  id="profileImage"
                  name="profileImage"
                  label={chooseAvatarLabel}
                  type="file"
                  form={null}
                  uploadImageError={uploadImageError}
                  disabled={uploadInProgress}
                >
                  {fieldProps => {
                    const { accept, id, input, label, disabled } = fieldProps;
                    const { name, type } = input;
                    const onChange = e => {
                      const file = e.target.files[0];
                      form.change(`profileImage`, file);
                      form.blur(`profileImage`);
                      if (file != null) {
                        const tempId = `${file.name}_${Date.now()}`;
                        this.props.onImageUpload({ id: tempId, file });
                      }
                    };
                    return (
                      <div className={css.uploadAvatarWrapper}>
                        <label className={css.label} htmlFor={id}>
                          {label}
                        </label>
                        <input
                          accept={accept}
                          id={id}
                          name={name}
                          className={css.uploadAvatarInput}
                          disabled={disabled}
                          onChange={onChange}
                          type={type}
                        />
                      </div>
                    );
                  }}
                </Field>
                {uploadImageFailed}
              </div>

              <div className={css.section}>
                <FieldTextInput
                  id="bio"
                  name="bio"
                  type="textarea"
                  label={intl.formatMessage({ id: 'CommunityProfilePromptForm.bioLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'CommunityProfilePromptForm.bioPlaceholder',
                  })}
                />
              </div>

              <div className={css.section}>
                <FieldSelect
                  id="discipline"
                  name="discipline"
                  label={intl.formatMessage({ id: 'CommunityProfilePromptForm.disciplineLabel' })}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'CommunityProfilePromptForm.disciplinePlaceholder' })}
                  </option>
                  {disciplinesConfig.map(d => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </FieldSelect>
              </div>

              <div className={css.section}>
                <FieldMultiSelectInput
                  id="role"
                  name="role"
                  options={rolesConfig.map(r => ({ key: r.value, label: r.label }))}
                  label={intl.formatMessage({ id: 'CommunityProfilePromptForm.roleLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'CommunityProfilePromptForm.rolePlaceholder',
                  })}
                />
              </div>

              {submitError}

              <div className={css.actions}>
                <Button
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                  ready={false}
                >
                  <FormattedMessage id="CommunityProfilePromptForm.saveAndContinue" />
                </Button>
                <button
                  type="button"
                  className={css.skipButton}
                  onClick={onSkip}
                  disabled={skipInProgress || submitInProgress || uploadInProgress}
                >
                  {skipInProgress ? (
                    <IconSpinner className={css.skipSpinner} />
                  ) : (
                    <FormattedMessage id="CommunityProfilePromptForm.skipForNow" />
                  )}
                </button>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

CommunityProfilePromptFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  uploadInProgress: false,
  updateInProgress: false,
  skipInProgress: false,
};

CommunityProfilePromptFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  skipInProgress: bool,
  onImageUpload: func.isRequired,
  onSkip: func.isRequired,
  uploadImageError: propTypes.error,
  updateProfileError: propTypes.error,
  intl: intlShape.isRequired,
};

const CommunityProfilePromptForm = compose(injectIntl)(CommunityProfilePromptFormComponent);

export default CommunityProfilePromptForm;
